import { useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { IconAlertTriangle, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import type {
  DetectedCategory,
  DetectedExtra,
  DetectedProduct,
} from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

import { V4 } from '../../../tokens';
import type { DuplicateGroup, DuplicateGroupReason } from '../types';

export interface DuplicateBannerProps {
  duplicateGroups: DuplicateGroup[];
  /**
   * Listas resueltas (post-dedupe) que el componente usa para hacer lookup
   * por ID y mostrar nombres / precios reales del REPRESENTANTE.
   * El padre las pasa tal cual del `analysis` consolidado.
   */
  categories: DetectedCategory[];
  products: DetectedProduct[];
  extras: DetectedExtra[];
  /**
   * Mapas pre-dedupe por ID. Necesarios para resolver nombres/precios de los
   * `candidateIds` de cada grupo — esos items NO están en `categories/products/extras`
   * porque `consolidate` los descartó. Sin estos mapas, el side-by-side de
   * duplicados mostraba IDs crudos ("qi_abc__prod_xyz") en lugar de los nombres
   * de los candidates (Seer bug 3378651276, MEDIUM).
   */
  preDedupeCategoriesById: Record<string, DetectedCategory>;
  preDedupeProductsById: Record<string, DetectedProduct>;
  preDedupeExtrasById: Record<string, DetectedExtra>;
  onDecide: (groupIndex: number, decision: 'keep_representative' | 'keep_all') => void;
}

type DuplicateDecision = 'keep_representative' | 'keep_all';

interface ResolvedItem {
  id: string;
  name: string;
  /** Texto secundario contextual (precio, categoría padre, etc.). */
  meta: string;
  /** Texto terciario opcional (descripción / categoría del producto). */
  hint?: string;
}

const REASON_LABEL: Record<DuplicateGroupReason, string> = {
  name_exact: 'Mismo nombre detectado',
  name_fuzzy: 'Nombres parecidos',
  name_and_price_close: 'Mismo nombre y precio similar',
};

const TYPE_LABEL_SINGULAR: Record<DuplicateGroup['type'], string> = {
  product: 'producto',
  category: 'categoría',
  extra: 'extra',
};

const formatPrice = (price: number | undefined): string => {
  if (typeof price !== 'number' || Number.isNaN(price)) return 'Sin precio';
  return `$${price.toFixed(2)}`;
};

/**
 * Construye el texto secundario contextual mostrado bajo el nombre.
 * Para productos: precio · categoría. Para extras: precio. Para categorías: nada.
 * @param {Object} group Grupo de duplicados (define el tipo de entidad).
 * @param {string} id ID sintético del item a resolver.
 * @param {Object} productsById Mapa de productos por ID.
 * @param {Object} extrasById Mapa de extras por ID.
 * @param {Object} categoriesById Mapa de categorías por ID.
 * @returns {Object} Texto principal y opcional secundario.
 */
const buildMeta = (
  group: DuplicateGroup,
  id: string,
  productsById: Map<string, DetectedProduct>,
  extrasById: Map<string, DetectedExtra>,
  categoriesById: Map<string, DetectedCategory>,
): { meta: string; hint?: string } => {
  if (group.type === 'product') {
    const prod = productsById.get(id);
    if (!prod) return { meta: '' };
    const cat = prod.category ? categoriesById.get(prod.category) : undefined;
    return {
      meta: formatPrice(prod.price),
      hint: cat ? `en ${cat.name}` : undefined,
    };
  }
  if (group.type === 'extra') {
    const ex = extrasById.get(id);
    if (!ex) return { meta: '' };
    return { meta: formatPrice(ex.price) };
  }
  return { meta: '' };
};

/**
 * Resuelve el nombre real de un item por su ID sintetico segun el tipo del grupo.
 * Si no encuentra match (orfano), devuelve el ID como fallback (mejor que crash).
 * @param {Object} group Grupo de duplicados (define el tipo de entidad).
 * @param {string} id ID del item a resolver.
 * @param {Object} productsById Mapa de productos por ID.
 * @param {Object} extrasById Mapa de extras por ID.
 * @param {Object} categoriesById Mapa de categorias por ID.
 * @returns {string} Nombre real del item o el ID si no se encuentra.
 */
const resolveName = (
  group: DuplicateGroup,
  id: string,
  productsById: Map<string, DetectedProduct>,
  extrasById: Map<string, DetectedExtra>,
  categoriesById: Map<string, DetectedCategory>,
): string => {
  if (group.type === 'product') return productsById.get(id)?.name ?? id;
  if (group.type === 'extra') return extrasById.get(id)?.name ?? id;
  return categoriesById.get(id)?.name ?? id;
};

const containerStyle: CSSProperties = {
  border: `1px solid ${V4.warning}`,
  background: 'rgba(254, 174, 51, 0.18)',
  color: V4.primary,
  borderRadius: 10,
  padding: 12,
  marginBottom: 16,
};

const toggleStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: V4.primary,
  fontWeight: 600,
  fontSize: 14,
  textAlign: 'left',
};

const groupCardStyle: CSSProperties = {
  background: V4.white,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  padding: 14,
  marginTop: 10,
};

const groupHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 4,
};

const reasonPillStyle: CSSProperties = {
  background: V4.grayUltraLight,
  color: V4.primary,
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  textTransform: 'none',
  letterSpacing: 0,
};

const sideBySideStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 10,
  marginTop: 8,
  marginBottom: 12,
};

const itemCardBaseStyle: CSSProperties = {
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  padding: 10,
  background: V4.white,
};

const itemBadgeBaseStyle: CSSProperties = {
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  padding: '2px 6px',
  borderRadius: 4,
  marginBottom: 6,
};

const radioGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginTop: 4,
};

const radioOptionBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  cursor: 'pointer',
  color: V4.primary,
  fontSize: 13,
  padding: '8px 10px',
  borderRadius: 6,
  border: `1px solid ${V4.grayLight}`,
  background: V4.white,
  transition: 'border-color 160ms cubic-bezier(.4,0,.2,1), background 160ms cubic-bezier(.4,0,.2,1)',
};

export const DuplicateBanner = ({
  duplicateGroups,
  categories,
  products,
  extras,
  preDedupeCategoriesById,
  preDedupeProductsById,
  preDedupeExtrasById,
  onDecide,
}: DuplicateBannerProps): ReactElement | null => {
  const [expanded, setExpanded] = useState(false);

  // Mergeamos post-dedupe (lista actual) + pre-dedupe (snapshot completo) en
  // un único lookup. Los items kept ya están en `categories/products/extras`
  // y también en los snapshots pre-dedupe — la unión es idempotente.
  // Esto cubre tanto al representante como a los candidates (que sólo viven
  // en los snapshots). Sin esto, los candidates renderizaban IDs crudos
  // (Seer bug 3378651276, MEDIUM).
  const categoriesById = useMemo(() => {
    const m = new Map<string, DetectedCategory>();
    for (const [id, c] of Object.entries(preDedupeCategoriesById)) m.set(id, c);
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories, preDedupeCategoriesById]);
  const productsById = useMemo(() => {
    const m = new Map<string, DetectedProduct>();
    for (const [id, p] of Object.entries(preDedupeProductsById)) m.set(id, p);
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products, preDedupeProductsById]);
  const extrasById = useMemo(() => {
    const m = new Map<string, DetectedExtra>();
    for (const [id, e] of Object.entries(preDedupeExtrasById)) m.set(id, e);
    for (const e of extras) m.set(e.id, e);
    return m;
  }, [extras, preDedupeExtrasById]);

  if (duplicateGroups.length === 0) {
    return null;
  }

  const ChevronIcon = expanded ? IconChevronUp : IconChevronDown;
  const total = duplicateGroups.length;

  return (
    <div data-testid="duplicate-banner" style={containerStyle}>
      <button
        type="button"
        data-testid="duplicate-banner-toggle"
        style={toggleStyle}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls="duplicate-banner-body"
      >
        <IconAlertTriangle size={18} color={V4.warning} aria-hidden="true" />
        <span style={{ flex: 1 }}>
          {total} {total === 1 ? 'posible duplicado' : 'posibles duplicados'} — toca para revisar
        </span>
        <ChevronIcon size={18} color={V4.primary} aria-hidden="true" />
      </button>

      {expanded ? (
        <div id="duplicate-banner-body" style={{ marginTop: 8 }}>
          <p
            style={{
              fontSize: 12,
              color: V4.grayUltraDark,
              margin: '4px 0 8px',
              lineHeight: 1.4,
            }}
          >
            Encontramos elementos parecidos en tus archivos. Por defecto conservamos uno solo de cada grupo. Cámbialo
            solo si quieres mantenerlos por separado.
          </p>

          {duplicateGroups.map((group, index) => {
            const decision: DuplicateDecision = group.decision === 'keep_all' ? 'keep_all' : 'keep_representative';
            const allIds = [group.representativeId, ...group.candidateIds];
            const resolved: ResolvedItem[] = allIds.map((id) => {
              const name = resolveName(group, id, productsById, extrasById, categoriesById);
              const { meta, hint } = buildMeta(group, id, productsById, extrasById, categoriesById);
              return { id, name, meta, hint };
            });
            const representative = resolved[0];
            const candidates = resolved.slice(1);
            const radioName = `dup-${index}`;
            const keepOneId = `${radioName}-keep-one`;
            const keepAllId = `${radioName}-keep-all`;
            const groupHeadingId = `${radioName}-heading`;

            return (
              <div
                key={`${group.type}-${group.representativeId}-${index}`}
                data-testid={`duplicate-group-${index}`}
                style={groupCardStyle}
              >
                <div style={groupHeaderStyle}>
                  <span id={groupHeadingId} style={{ fontWeight: 600, fontSize: 13 }}>
                    {TYPE_LABEL_SINGULAR[group.type].charAt(0).toUpperCase() + TYPE_LABEL_SINGULAR[group.type].slice(1)}
                    : {representative.name}
                  </span>
                  <span style={reasonPillStyle}>{REASON_LABEL[group.reason]}</span>
                </div>

                <div style={sideBySideStyle}>
                  <div
                    data-testid={`duplicate-group-${index}-representative`}
                    style={{
                      ...itemCardBaseStyle,
                      borderColor: decision === 'keep_representative' ? V4.secondary : V4.grayLight,
                    }}
                  >
                    <span
                      style={{
                        ...itemBadgeBaseStyle,
                        background: V4.secondary,
                        color: V4.white,
                      }}
                    >
                      Se conserva
                    </span>
                    <div style={{ fontWeight: 600, fontSize: 14, color: V4.primary }}>{representative.name}</div>
                    {representative.meta ? (
                      <div style={{ fontSize: 12, color: V4.grayUltraDark, marginTop: 2 }}>{representative.meta}</div>
                    ) : null}
                    {representative.hint ? (
                      <div style={{ fontSize: 11, color: V4.grayUltraDark, marginTop: 2 }}>{representative.hint}</div>
                    ) : null}
                  </div>

                  {candidates.map((cand, candIdx) => {
                    const willBeRemoved = decision === 'keep_representative';
                    return (
                      <div
                        key={`${cand.id}-${candIdx}`}
                        data-testid={`duplicate-group-${index}-candidate-${candIdx}`}
                        style={{
                          ...itemCardBaseStyle,
                          opacity: willBeRemoved ? 0.6 : 1,
                          borderColor: !willBeRemoved ? V4.secondary : V4.grayLight,
                        }}
                      >
                        <span
                          style={{
                            ...itemBadgeBaseStyle,
                            background: willBeRemoved ? V4.grayUltraLight : V4.accentMint,
                            color: willBeRemoved ? V4.grayUltraDark : V4.white,
                          }}
                        >
                          {willBeRemoved ? 'Se descarta' : 'Se conserva'}
                        </span>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: V4.primary,
                            textDecoration: willBeRemoved ? 'line-through' : 'none',
                          }}
                        >
                          {cand.name}
                        </div>
                        {cand.meta ? (
                          <div style={{ fontSize: 12, color: V4.grayUltraDark, marginTop: 2 }}>{cand.meta}</div>
                        ) : null}
                        {cand.hint ? (
                          <div style={{ fontSize: 11, color: V4.grayUltraDark, marginTop: 2 }}>{cand.hint}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                <div
                  role="radiogroup"
                  aria-labelledby={groupHeadingId}
                  data-testid={`duplicate-group-${index}-decision`}
                  style={radioGroupStyle}
                >
                  <label
                    htmlFor={keepOneId}
                    style={{
                      ...radioOptionBaseStyle,
                      borderColor: decision === 'keep_representative' ? V4.secondary : V4.grayLight,
                      background: decision === 'keep_representative' ? V4.grayUltraLight : V4.white,
                    }}
                  >
                    <input
                      id={keepOneId}
                      type="radio"
                      name={radioName}
                      data-testid={`duplicate-group-${index}-keep-one`}
                      checked={decision === 'keep_representative'}
                      onChange={() => onDecide(index, 'keep_representative')}
                    />
                    <span>
                      <span style={{ fontWeight: 600 }}>Conservar uno solo</span>
                      <span style={{ color: V4.grayUltraDark, marginLeft: 6, fontWeight: 400 }}>(recomendado)</span>
                      <div style={{ fontSize: 12, color: V4.grayUltraDark, marginTop: 2 }}>
                        Mantenemos &laquo;{representative.name}&raquo; y descartamos {candidates.length}{' '}
                        {candidates.length === 1 ? 'duplicado' : 'duplicados'}.
                      </div>
                    </span>
                  </label>

                  <label
                    htmlFor={keepAllId}
                    style={{
                      ...radioOptionBaseStyle,
                      borderColor: decision === 'keep_all' ? V4.secondary : V4.grayLight,
                      background: decision === 'keep_all' ? V4.grayUltraLight : V4.white,
                    }}
                  >
                    <input
                      id={keepAllId}
                      type="radio"
                      name={radioName}
                      data-testid={`duplicate-group-${index}-keep-all`}
                      checked={decision === 'keep_all'}
                      onChange={() => onDecide(index, 'keep_all')}
                    />
                    <span>
                      <span style={{ fontWeight: 600 }}>Conservar ambos por separado</span>
                      <div style={{ fontSize: 12, color: V4.grayUltraDark, marginTop: 2 }}>
                        Crea {allIds.length} {TYPE_LABEL_SINGULAR[group.type]}
                        {allIds.length === 1 ? '' : 's'} aunque se vean similares.
                      </div>
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
